import { getValue } from 'mobx-async'

import { formatDate } from '@avantstay/backoffice-core'

import { ArrivalsModeFilter } from '@new/__global/components/FastTapeChart/components/TapeChartFilters/ArrivalsModeFilter'
import {
  AddressRadiusFilter,
  AmenitiesFilter,
  BathroomsRangeFilter,
  BedroomsRangeFilter,
  DepositsOnHoldFilter,
  GuestsRangeFilter,
  HighlightsFilter,
  PetPolicyFilter,
  PriceRangeFilter,
  RevenueModelFilter,
  RiskLevelFilter,
  SeasonGroupFilter,
} from '@new/__global/components/Filters'
import { GuestReadinessFilter } from '@new/__global/components/Filters/GuestReadinessFilter'
import { ListingAccountStatusFilter } from '@new/__global/components/Filters/ListingAccountStatusFilter'
import { ListingStatusFilter } from '@new/__global/components/Filters/ListingStatusFilter'
import { ExtendedColumnKey, FilterConfig } from '@new/__global/components/FiltersFramework'
import {
  addressParamParser,
  arrayParamsFormatterAndParser,
  booleanParamParser,
  formatAddressPreview,
  formatPriceRangePreview,
  formatQuantityRangePreview,
  optionsFilterPreviewFormatter,
  priceRangeParamParser,
  rangeParamParser,
  stringParamParser,
} from '@new/__global/components/FiltersFramework/_utils'
import { RegionsFilter } from '@new/__global/components/RegionsFilter'
import { filtersLabels } from '@new/domains/bookings/screens/bookingsList/utils/labels'
import { BookingsTapeChartStore } from '@new/domains/bookingsTapeChart/stores/BookingsTapeChartStore'
import { BookingsColorMode } from '@new/domains/fastBookingsTapeChart/__types'
import {
  formatDateRange,
  parseDateRange,
  parseDateRangeParam,
} from '@new/domains/fastBookingsTapeChart/__utils'
import { BookingIssuesFilter } from '@new/domains/fastBookingsTapeChart/components/TapeChartFilters/BookingIssuesFilter'
import { CheckInDatesFilter } from '@new/domains/fastBookingsTapeChart/components/TapeChartFilters/CheckInDatesFilter'
import { ColorByFilter } from '@new/domains/fastBookingsTapeChart/components/TapeChartFilters/ColorByFilter'
import { OperationStatusFilter } from '@new/domains/fastBookingsTapeChart/components/TapeChartFilters/OperationStatusFilter'
import { PaymentStatusFilter } from '@new/domains/fastBookingsTapeChart/components/TapeChartFilters/PaymentStatusFilter'
import { RatesFilter } from '@new/domains/fastBookingsTapeChart/components/TapeChartFilters/RatesFilter'
import { ReservationStatusFilter } from '@new/domains/fastBookingsTapeChart/components/TapeChartFilters/ReservationStatusFilter'
import { ViewFromFilter } from '@new/domains/fastBookingsTapeChart/components/TapeChartFilters/ViewFromFilter'
import {
  DEFAULT_VIEW_FROM_DATE,
  ISO_DATE_FORMAT,
} from '@new/domains/fastBookingsTapeChart/config/contants'
import {
  AvailableDatesFilter,
  fastBookingsTapeChartFiltersStore,
} from '@new/domains/fastBookingsTapeChart/internal'
import { tapechartGridSizingStore } from '@new/domains/fastBookingsTapeChart/stores/TapechartGridSizingStore'
import { SeasonsStore } from '@new/domains/finance/stores/SeasonsStore'

export enum HomeFilterIds {
  Amenities = 'amenityIds',
  Highlights = 'highlights',
  MinBathrooms = 'minBathrooms',
  MinBedrooms = 'minBedrooms',
  MinPets = 'minPets',
  Regions = 'regionIds',
  RevenueModel = 'revenueModel',
  RiskLevel = 'riskLevel',
  SeasonGroups = 'seasonGroupIds',
  ShowOffBoarded = 'withOffboarded',
  PetPolicy = 'petPolicy',
  DateRange = 'dateRange',
  PriceRange = 'priceRange',
  MinOccupancy = 'minOccupancy',
  Guests = 'guests',
  BedroomsRange = 'bedroomsRange',
  BathroomsRange = 'bathrooms',
  AddressRadius = 'addressRadius',
  AccountStatus = 'accountStatus',
  GuestReadinessStatus = 'guestReadinessStatus',
  ListingStatus = 'listingStatus',
  OperationReadinessStatus = 'operationReadinessStatus',
  CheckInDates = 'checkInDates',
}

export enum BookingFilterIds {}

export enum ViewStateFilterIds {
  BookingIssues = 'bookingIssues',
  ColorBy = 'colorBy',
  DepositsOnHold = 'depositsOnHold',
  ViewFrom = 'viewFrom',
  NightlyRates = 'rates',
  PaymentStatus = 'paymentStatus',
  ReservationStatus = 'status',
  ArrivalsMode = 'arrivalsMode',
}

export enum CustomFilterIds {
  AvailableDates = 'availableDates',
}

export enum FilterGroup {
  properties = 'properties',
  bookings = 'bookings',
}

export const fastTapeChartFiltersConfig: FilterConfig<any>[] = [
  {
    id: HomeFilterIds.Regions,
    label: 'Markets',
    queryParamName: 'markets',
    ...arrayParamsFormatterAndParser,
    showOnTheTopFiltersBar: () => true,
    component: RegionsFilter,
  },
  {
    id: ViewStateFilterIds.ViewFrom,
    label: 'View From',
    queryParamName: 'view-from',
    showOnTheTopFiltersBar: () => true,
    component: ViewFromFilter,
    defaultValue: formatDate(DEFAULT_VIEW_FROM_DATE, ISO_DATE_FORMAT),
  },
  {
    id: CustomFilterIds.AvailableDates,
    label: 'Show Available',
    queryParamName: 'available-dates',
    showOnTheTopFiltersBar: () => true,
    parser: parseDateRange,
    formatter: formatDateRange,
    component: AvailableDatesFilter,
  },
  {
    id: ViewStateFilterIds.NightlyRates,
    label: 'Nightly Rates',
    queryParamName: 'show-rates',
    ...booleanParamParser,
    showOnTheTopFiltersBar: () => true,
    component: RatesFilter,
  },
  {
    id: ViewStateFilterIds.ColorBy,
    label: 'Color By',
    queryParamName: 'color-by',
    showOnTheTopFiltersBar: () => false,
    showOnTheAppliedFilters: () => false,
    showOnTheSidePanel: () => tapechartGridSizingStore.isMobile,
    defaultValue: BookingsColorMode.ReservationStatus,
    component: ColorByFilter,
  },
  // Bookings Filters
  {
    id: HomeFilterIds.CheckInDates,
    label: 'Check-In Date',
    queryParamName: 'check-in-dates',
    showOnTheTopFiltersBar: () => false,
    parser: parseDateRange,
    filterPreviewFormatter: parseDateRangeParam,
    formatter: formatDateRange,
    component: CheckInDatesFilter,
    groupId: FilterGroup.bookings,
  },
  {
    id: ViewStateFilterIds.DepositsOnHold,
    label: 'Deposits on Hold',
    queryParamName: 'deposit-on-hold',
    component: props => <DepositsOnHoldFilter {...props} />,
    showOnTheTopFiltersBar: () => false,
    groupId: FilterGroup.bookings,
  },
  {
    id: ViewStateFilterIds.BookingIssues,
    label: 'Booking Issues',
    queryParamName: 'issues',
    component: BookingIssuesFilter,
    ...arrayParamsFormatterAndParser,
    showOnTheTopFiltersBar: () => false,
    groupId: FilterGroup.bookings,
  },
  {
    id: ViewStateFilterIds.ReservationStatus,
    label: 'Booking Status',
    queryParamName: 'reservation',
    ...arrayParamsFormatterAndParser,
    showOnTheTopFiltersBar: () => false,
    component: ReservationStatusFilter,
    groupId: FilterGroup.bookings,
  },
  {
    id: ViewStateFilterIds.PaymentStatus,
    label: 'Payment Status',
    queryParamName: 'payment-status',
    ...arrayParamsFormatterAndParser,
    component: PaymentStatusFilter,
    showOnTheTopFiltersBar: () => false,
    groupId: FilterGroup.bookings,
  },
  // Properties Filters
  {
    id: HomeFilterIds.Guests,
    label: 'Guests',
    queryParamName: 'guests',
    ...rangeParamParser,
    component: props => <GuestsRangeFilter {...props} />,
    showOnTheTopFiltersBar: () => false,
    filterPreviewFormatter: formatQuantityRangePreview,
    groupId: FilterGroup.properties,
    filterAsColumnKey: ExtendedColumnKey.maxOccupancy,
  },
  {
    id: HomeFilterIds.PriceRange,
    label: 'Nightly Rates Subtotal',
    queryParamName: 'price',
    ...priceRangeParamParser,
    component: props => <PriceRangeFilter {...props} />,
    showOnTheTopFiltersBar: () => false,
    filterPreviewFormatter: formatPriceRangePreview,
    groupId: FilterGroup.properties,
  },
  {
    id: HomeFilterIds.BedroomsRange,
    label: filtersLabels.bedroomsRange,
    queryParamName: 'bedrooms',
    ...rangeParamParser,
    component: props => <BedroomsRangeFilter {...props} />,
    showOnTheTopFiltersBar: () => false,
    filterPreviewFormatter: formatQuantityRangePreview,
    groupId: FilterGroup.properties,
    filterAsColumnKey: ExtendedColumnKey.bedroomsCount,
  },
  {
    id: HomeFilterIds.BathroomsRange,
    label: filtersLabels.bathroomsRange,
    queryParamName: 'bathrooms',
    ...rangeParamParser,
    component: props => <BathroomsRangeFilter {...props} />,
    showOnTheTopFiltersBar: () => false,
    filterPreviewFormatter: formatQuantityRangePreview,
    groupId: FilterGroup.properties,
    filterAsColumnKey: ExtendedColumnKey.bathroomsCount,
  },
  {
    id: HomeFilterIds.AddressRadius,
    label: filtersLabels.addressRadius,
    queryParamName: 'address',
    ...addressParamParser,
    component: props => <AddressRadiusFilter {...props} />,
    showOnTheTopFiltersBar: () => false,
    filterPreviewFormatter: formatAddressPreview,
    groupId: FilterGroup.properties,
    filterAsColumnKey: ExtendedColumnKey.distance,
  },
  {
    id: HomeFilterIds.Amenities,
    label: 'Amenities',
    queryParamName: 'amenities',
    ...arrayParamsFormatterAndParser,
    ...optionsFilterPreviewFormatter(() => getValue(BookingsTapeChartStore.amenitiesOptions)),
    showOnTheTopFiltersBar: () => false,
    component: props => <AmenitiesFilter {...props} />,
    groupId: FilterGroup.properties,
  },
  {
    id: HomeFilterIds.PetPolicy,
    label: 'Pet Policy',
    queryParamName: 'pets',
    component: props => <PetPolicyFilter {...props} />,
    showOnTheTopFiltersBar: () => false,
    groupId: FilterGroup.properties,
    filterAsColumnKey: ExtendedColumnKey.petPolicy,
  },
  {
    id: HomeFilterIds.AccountStatus,
    label: 'Account Status',
    queryParamName: 'account-status',
    ...arrayParamsFormatterAndParser,
    component: props => <ListingAccountStatusFilter {...props} />,
    showOnTheTopFiltersBar: () => false,
    groupId: FilterGroup.properties,
  },
  {
    id: HomeFilterIds.GuestReadinessStatus,
    label: 'Guest Readiness Status',
    queryParamName: 'guest-readiness-status',
    ...arrayParamsFormatterAndParser,
    component: props => <GuestReadinessFilter {...props} />,
    showOnTheTopFiltersBar: () => false,
    groupId: FilterGroup.properties,
  },
  {
    id: HomeFilterIds.ListingStatus,
    label: 'Listing Status',
    queryParamName: 'listing-status',
    ...arrayParamsFormatterAndParser,
    component: props => <ListingStatusFilter {...props} />,
    showOnTheTopFiltersBar: () => false,
    groupId: FilterGroup.properties,
  },

  {
    id: HomeFilterIds.RevenueModel,
    label: 'Revenue Model',
    queryParamName: 'revenue-model',
    ...arrayParamsFormatterAndParser,
    component: props => <RevenueModelFilter {...props} />,
    showOnTheTopFiltersBar: () => false,
    groupId: FilterGroup.properties,
    filterAsColumnKey: ExtendedColumnKey.revenueModel,
  },
  {
    id: HomeFilterIds.RiskLevel,
    label: 'Risk Level',
    queryParamName: 'risk-level',
    ...arrayParamsFormatterAndParser,
    component: props => <RiskLevelFilter {...props} />,
    customComponentProps: {
      title: 'Risk Level',
    },
    showOnTheTopFiltersBar: () => false,
    groupId: FilterGroup.properties,
  },
  {
    id: HomeFilterIds.SeasonGroups,
    label: 'Season Groups',
    queryParamName: 'season-groups',
    ...arrayParamsFormatterAndParser,
    component: props => <SeasonGroupFilter {...props} />,
    ...optionsFilterPreviewFormatter(() => getValue(SeasonsStore.seasonGroupsOptions)),
    showOnTheTopFiltersBar: () => false,
    groupId: FilterGroup.properties,
  },
  {
    id: HomeFilterIds.Highlights,
    label: 'Highlights',
    queryParamName: 'highlights',
    ...arrayParamsFormatterAndParser,
    component: props => <HighlightsFilter {...props} />,
    ...optionsFilterPreviewFormatter(() => getValue(BookingsTapeChartStore.highlightsOptions)),
    showOnTheTopFiltersBar: () => false,
    groupId: FilterGroup.properties,
  },
  {
    id: HomeFilterIds.OperationReadinessStatus,
    label: 'Operation Status',
    queryParamName: 'operation-readiness-status',
    ...stringParamParser,
    component: props => <OperationStatusFilter {...props} />,
    showOnTheTopFiltersBar: () => false,
    showOnTheSidePanel: () => false,
  },
  {
    id: ViewStateFilterIds.ArrivalsMode,
    label: 'Arrivals Mode',
    queryParamName: 'arrivals-mode',
    ...booleanParamParser,
    component: props => (
      <ArrivalsModeFilter
        {...props}
        onChange={fastBookingsTapeChartFiltersStore.toggleArrivalMode}
      />
    ),
    defaultValue: false,
    showOnTheTopFiltersBar: () => false,
    showOnTheSidePanel: () => false,
    showOnTheAppliedFilters: () => false,
  },
]
